import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

interface PrayerTagsProps {
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  disabled?: boolean;
  availableTags?: string[];
}

const DEFAULT_TAGS = [
  'Healing', 'Guidance', 'Protection', 'Thanksgiving', 
  'Wisdom', 'Forgiveness', 'Strength', 'Peace',
  'Family', 'Work', 'Health', 'Relationships'
];

export default function PrayerTags({
  selectedTags,
  setSelectedTags,
  disabled = false,
  availableTags = DEFAULT_TAGS
}: PrayerTagsProps) {
  // Defensive: ensure selectedTags is always an array
  const selected = Array.isArray(selectedTags) ? selectedTags : [];
  const tags = Array.isArray(availableTags) && availableTags.length > 0 ? availableTags : DEFAULT_TAGS;

  const toggleTag = (tag: string) => {
    if (disabled) return;

    if (selected.includes(tag)) {
      setSelectedTags(selected.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selected, tag]);
    }
  };

  // Show first 1 tag directly, rest in dropdown
  const visibleTags = tags.slice(0, 1);
  const dropdownTags = tags.slice(1);

  return (
    <div className="space-y-1">
      <Label className="text-black dark:text-white font-medium text-xs">
        Category
      </Label>
      <div className="flex flex-wrap gap-1">
        {/* First 1 visible tag - reduced padding */}
        {visibleTags.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => toggleTag(tag)}
            disabled={disabled}
            aria-pressed={selected.includes(tag)}
            className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
              selected.includes(tag)
                ? 'bg-app-gold text-black'
                : 'bg-gray-200 dark:bg-gray-700 text-black/80 dark:text-white/80 hover:bg-gray-300 dark:hover:bg-gray-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {tag}
          </button>
        ))}

        {/* More dropdown for remaining tags - reduced size */}
        {dropdownTags.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                aria-label="Show more prayer categories"
                className="px-2 py-0.5 h-auto rounded-full text-xs font-medium border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="start" 
              className="max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md"
            >
              {dropdownTags.map((tag) => (
                <DropdownMenuItem
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  aria-checked={selected.includes(tag)}
                  className={`cursor-pointer ${
                    selected.includes(tag)
                      ? 'bg-app-gold/20 text-app-gold'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-black dark:text-white'
                  }`}
                >
                  {tag}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
